import { create } from 'zustand'
import { useShallow } from 'zustand/react/shallow'
import type {
  AppState,
  ViewName,
  TimeWindow,
  ActiveFilters,
  StreamSnapshot,
  Incident,
  Dimension,
  DrillNode,
  OverlayPanel,
} from '@/types'
import type { SearchSliceEnvelope, SearchSliceNode, StreamSliceNode } from '@/analysis/searchContract'
import type { WorldFlowEnvelope } from '@/analysis/worldFlowTypes'
import { sliceRowKey } from '@/analysis/searchContract'

const DEFAULT_FILTERS: ActiveFilters = {
  continent: [],
  country: [],
  region: [],
  city: [],
  studio: [],
  game: [],
  brand: [],
  streamKey: [],
  sourceUrl: [],
  streamType: [],
  browser: [],
  os: [],
  deviceType: [],
  platform: [],
  cdnEdge: [],
  severity: [0, 1, 2],
  userId: null,
}

interface AppStore extends AppState {
  setView: (view: ViewName) => void
  toggleSidebar: () => void
  setSidebarOpen: (open: boolean) => void
  setDimension: (d: Dimension) => void
  setTableOpen: (open: boolean) => void
  toggleTable: () => void
  setTableH: (h: number) => void
  worldIncidentsOpen: boolean
  toggleWorldIncidents: () => void
  /** Hop visibility on the connected pipeline map (all on by default). */
  worldLayers: { studios: boolean; edges: boolean; clients: boolean; flows: boolean }
  toggleWorldLayer: (key: 'studios' | 'edges' | 'clients' | 'flows') => void
  worldSegment: 'overall' | 'delivery' | 'client'
  setWorldSegment: (segment: 'overall' | 'delivery' | 'client') => void
  /** Client cluster grain on World map: continent / country rollup or city (default). Server-aggregated. */
  worldClientGrain: 'continent' | 'country' | 'city'
  setWorldClientGrain: (grain: 'continent' | 'country' | 'city') => void
  drillToRegion: (region: string) => void
  drillToNode: (node: NonNullable<DrillNode>) => void
  clearDrillNode: () => void
  /** Studio/game tile click: filter to that entity and flip to Stream mode. */
  descendToStreams: (filterKey: 'studio' | 'game' | 'brand', value: string) => void
  /** Global stream listing: Hex in Stream mode with no geo scope. */
  viewAllStreams: () => void
  /** Toggle Active only vs Include inactive (1h vs 6h offline grace). */
  toggleIncludeInactive: () => void
  drillToStream: (sid: string) => void
  clearDrill: () => void
  toggleFilterValue: (key: Exclude<keyof ActiveFilters, 'severity' | 'userId'>, val: string) => void
  /** Replace a filter axis with a single value (or clear it when val is null). */
  setFilterOnly: (key: Exclude<keyof ActiveFilters, 'severity' | 'userId'>, val: string | null) => void
  /** Replace a multi-value filter with an explicit list (cluster activate-all). */
  setFilterValues: (key: Exclude<keyof ActiveFilters, 'severity' | 'userId'>, vals: string[]) => void
  clearFilter: (key: Exclude<keyof ActiveFilters, 'severity'>) => void
  /** Clear continent / country / region / city so Hex scope is non-geo. */
  clearGeoFilters: () => void
  /**
   * Set one client geo grain and clear the others (city/country/continent/region).
   * Used by World client-node and arc drills into Hex.
   */
  setClientGeoFilter: (
    next:
      | { grain: 'continent'; continent: string }
      | { grain: 'country'; country: string }
      | { grain: 'city'; city: string; country?: string },
  ) => void
  setUserId: (val: string | null) => void
  toggleSeverity: (sev: number | null) => void
  clearFilters: () => void
  /** After REST + matching WS `subscribed`, allow snapshot merges again. */
  tryReleaseDataGate: () => void
  setTimeMode: (mode: 'live' | 'historic') => void
  setWindow: (w: TimeWindow) => void
  setHistoricTs: (ts: number | null) => void
  /** Pin an exact [from, to] range for dashboard demos (sets window=custom). */
  setHistoricRange: (fromTs: number, toTs: number) => void
  setOverlayPanel: (panel: OverlayPanel) => void
  toggleOverlayPanel: (panel: Exclude<OverlayPanel, null>) => void
  syncToIncident: (incidentId: string, ts: string, sid: string) => void
  clearIncidentSync: () => void
  updateStream: (snapshot: StreamSnapshot) => void
  // Global streams snapshot (never replaced by drilldown)
  setStreams: (items: StreamSnapshot[]) => void
  // Drilldown subset for Hex stream view
  setStreamsDrill: (items: StreamSnapshot[]) => void
  clearStreamsDrill: () => void
  setPipelineLatency: (ms: number) => void
  pipelineLatencyName: string | null
  setPipelineLatencyName: (name: string | null) => void
  setNowTs: (ts: number) => void
  setDataDebouncing: (on: boolean) => void
  setStreamsLoading: (on: boolean) => void
  setAnalysisLoading: (on: boolean) => void
  setLastDataError: (msg: string | null) => void
  markStreamsUpdated: (ts?: number) => void
  addIncident: (inc: Incident) => void
  setIncidents: (incidents: Incident[]) => void
  setDimensions: (dims: Record<string, any[]>) => void
  comparison: AppState['comparison']
  addComparisonStream: (sid: string) => void
  removeComparisonStream: (sid: string) => void
  toggleComparisonVisible: (sid: string) => void
  clearComparison: () => void

  /** Latest search envelope (dimension or stream list); WS `snapshot` updates the same shape. */
  searchResult: SearchSliceEnvelope | null
  setSearchResult: (r: AppStore['searchResult']) => void

  /** Connected World pipeline flow (hops + links); REST warmup + WS `world`. */
  worldFlowResult: WorldFlowEnvelope | null
  setWorldFlowResult: (r: AppStore['worldFlowResult']) => void

  /** Hex stream drill: stream slices scoped to the drilled geo node. */
  searchResultDrill: SearchSliceEnvelope | null
  setSearchResultDrill: (r: AppStore['searchResultDrill']) => void
}

export const useStore = create<AppStore>((set) => ({
  view: 'world',
  // Default hidden on load per UX request.
  sidebarOpen: false,
  // Default Hex grouping mode. World view always queries continent geo aggregates
  // regardless of this value (see buildQuery).
  dimension: 'stream',
  tableOpen: false,
  tableH: 280,
  worldIncidentsOpen: false,
  worldLayers: { studios: true, edges: true, clients: true, flows: true },
  worldSegment: 'overall',
  worldClientGrain: 'continent',
  drillRegion: null,
  drillStream: null,
  drillNode: null,
  filters: DEFAULT_FILTERS,
  includeInactive: false,
  timeMode: 'live',
  window: '1h',
  historicTs: null,
  historicFromTs: null,
  overlayPanel: null,
  incidentSync: null,
  streams: [],
  streamsDrill: [],
  continents: [],
  incidents: [],
  dimensions: {},
  comparison: { streams: {}, visible: {}, maxItems: 6 },
  pipelineLatencyMs: null,
  pipelineLatencyName: null,
  nowTs: Date.now(),
  dataDebouncing: false,
  streamsLoading: false,
  analysisLoading: false,
  lastDataError: null,
  lastStreamsUpdateTs: null,
  wsUiPaused: false,
  activeDataKey: null,
  restReadyKey: null,
  wsSubscribedKey: null,
  searchResult: null,
  worldFlowResult: null,
  searchResultDrill: null,

  setView: (view) => set((s) => ({
    view,
    // Do not clear drillStream when navigating to DeepDive via sidebar;
    // DeepDive uses drillStream with `POST /api/analysis` and the shared search WebSocket.
    drillStream: view === 'deep' ? s.drillStream : null,
    overlayPanel: null,
    lastStreamsUpdateTs: null,
  })),

  toggleSidebar: () => set((s) => ({ sidebarOpen: !s.sidebarOpen })),
  setSidebarOpen: (open) => set({ sidebarOpen: open }),
  setDimension: (d) => set({
    dimension: d,
    drillRegion: null,
    drillStream: null,
    drillNode: null,
    // Wipe cached envelopes so the table can't render stale-kind rows during
    // the brief window before the new REST/WS data arrives.
    searchResult: null,
    searchResultDrill: null,
    worldFlowResult: null,
  }),
  setTableOpen: (open) => set({ tableOpen: open }),
  toggleTable: () => set((s) => ({ tableOpen: !s.tableOpen })),
  setTableH: (h) => set({ tableH: h }),
  toggleWorldIncidents: () => set((s) => ({ worldIncidentsOpen: !s.worldIncidentsOpen })),
  toggleWorldLayer: (key) => set((s) => ({
    worldLayers: { ...s.worldLayers, [key]: !s.worldLayers[key] },
  })),
  setWorldSegment: (segment) => set({ worldSegment: segment }),
  // Grain drives server-side re-aggregation (same granularity buckets incident
  // detection and other consumers use), so clear the result to refetch.
  // Grain drives a cheap re-subscribe to the precomputed world snapshot.
  setWorldClientGrain: (grain) => set({ worldClientGrain: grain, worldFlowResult: null }),

  drillToRegion: (region) => {
    // Normalise legacy/alt region codes (fixes cases like "OT" being used for Oceania).
    const norm = region === 'OT' ? 'OC' : region
    return set({
      view: 'hex',
      // Scope-only gesture: Hex opens in whatever mode is active (geo×mode
      // pair slices back studio/game; stream mode keeps prior behavior).
      drillRegion: norm,
      drillStream: null,
      drillNode: { dimension: 'region', value: norm },
      searchResult: null,
      searchResultDrill: null,
    })
  },

  drillToNode: (node) => set({
    view: 'hex',
    drillNode: node,
    drillStream: null,
    drillRegion: node.dimension === 'region' ? node.value : null,
    streamsDrill: [],
    searchResult: null,
    searchResultDrill: null,
  }),

  // Ladder descent: a studio/game hex tile narrows to that entity's streams.
  // The one legitimate mode change on click — you moved a rung down, and the
  // breadcrumb shows both the geo scope and the new entity filter.
  descendToStreams: (filterKey, value) => set((s) => ({
    dimension: 'stream',
    filters: { ...s.filters, [filterKey]: value ? [value] : [] },
    drillStream: null,
    searchResult: null,
    searchResultDrill: null,
  })),

  viewAllStreams: () => set({
    view: 'hex',
    dimension: 'stream',
    drillNode: null,
    drillRegion: null,
    drillStream: null,
    searchResult: null,
    searchResultDrill: null,
  }),

  toggleIncludeInactive: () => set((s) => ({ includeInactive: !s.includeInactive })),

  clearDrillNode: () => set({
    drillNode: null,
    drillRegion: null,
    searchResult: null,
    searchResultDrill: null,
  }),

  drillToStream: (sid) => set({
    view: 'deep',
    drillStream: sid,
    searchResult: null,
    searchResultDrill: null,
  }),

  clearDrill: () => set({
    drillRegion: null,
    drillStream: null,
    drillNode: null,
    view: 'world',
    // Keep Hex mode (stream/studio/game); World ignores it for its geo query.
    searchResult: null,
    searchResultDrill: null,
    worldFlowResult: null,
  }),

  toggleFilterValue: (key, val) => set((s) => {
    const cur = s.filters[key] as string[]
    const next = cur.includes(val) ? cur.filter(v => v !== val) : [...cur, val]
    return { filters: { ...s.filters, [key]: next } }
  }),

  setFilterOnly: (key, val) => set((s) => ({
    filters: { ...s.filters, [key]: val ? [val] : [] },
  })),

  setFilterValues: (key, vals) => set((s) => ({
    filters: {
      ...s.filters,
      [key]: vals.map((v) => String(v).trim()).filter(Boolean),
    },
  })),

  clearFilter: (key) => set((s) => ({
    filters: {
      ...s.filters,
      [key]: key === 'userId' ? null : [],
    } as ActiveFilters,
  })),

  clearGeoFilters: () => set((s) => ({
    filters: {
      ...s.filters,
      continent: [],
      country: [],
      region: [],
      city: [],
    },
  })),

  setClientGeoFilter: (next) => set((s) => {
    const geo = {
      continent: [] as string[],
      country: [] as string[],
      region: [] as string[],
      city: [] as string[],
    }
    if (next.grain === 'continent') {
      geo.continent = next.continent ? [next.continent] : []
    } else if (next.grain === 'country') {
      geo.country = next.country ? [next.country] : []
    } else {
      geo.city = next.city ? [next.city] : []
      geo.country = next.country ? [next.country] : []
    }
    return { filters: { ...s.filters, ...geo } }
  }),

  tryReleaseDataGate: () => set((s) => {
    const key = s.activeDataKey
    if (!key || s.restReadyKey !== key || s.wsSubscribedKey !== key) return {}
    return { wsUiPaused: false, streamsLoading: false }
  }),

  setUserId: (val) => set((s) => ({ filters: { ...s.filters, userId: val } })),

  toggleSeverity: (sev) => set((s) => {
    const cur = s.filters?.severity
    const next = cur.includes(sev as never)
      ? cur.filter((v) => v !== sev)
      : [...cur, sev]
    return { filters: { ...s.filters, severity: next as (0|1|2|null)[] } }
  }),

  clearFilters: () => set({ filters: DEFAULT_FILTERS }),

  setTimeMode: (mode) => set({
    timeMode: mode,
    ...(mode === 'live'
      ? { historicTs: null, historicFromTs: null, incidentSync: null, window: '1h' as const }
      : {})
  }),

  setWindow: (w) => set((s) => {
    if (w === 'custom') {
      const to = s.historicTs ?? s.nowTs
      const from = s.historicFromTs ?? to - 10_000
      return {
        window: 'custom',
        timeMode: 'historic' as const,
        historicTs: to,
        historicFromTs: from,
      }
    }
    return { window: w, historicFromTs: null }
  }),

  setHistoricTs: (ts) => set({
    historicTs: ts,
    timeMode: ts ? 'historic' : 'live',
    ...(ts == null ? { historicFromTs: null } : {}),
  }),

  setHistoricRange: (fromTs, toTs) => {
    const from = Math.min(fromTs, toTs)
    const to = Math.max(fromTs, toTs)
    set({
      timeMode: 'historic',
      window: 'custom',
      historicFromTs: from,
      historicTs: to,
      incidentSync: null,
    })
  },

  setOverlayPanel: (panel) => set({ overlayPanel: panel }),
  toggleOverlayPanel: (panel) =>
    set((s) => ({ overlayPanel: s.overlayPanel === panel ? null : panel })),

  syncToIncident: (incidentId, ts, sid) => set({
    incidentSync: { incidentId: String(incidentId), ts: new Date(ts).getTime().toString() as string, sid },
    historicTs: new Date(ts).getTime(),
    historicFromTs: null,
    timeMode: 'historic',
    window: '1h',
    view: 'deep',
    drillStream: sid,
  }),

  clearIncidentSync: () => set({
    incidentSync: null,
    historicTs: null,
    historicFromTs: null,
    timeMode: 'live',
  }),

  updateStream: (snapshot) => set((s) => ({
    streams: s.streams.some((st) => st.sid === snapshot.sid)
      ? s.streams.map((st) => st.sid === snapshot.sid ? snapshot : st)
      : [...s.streams, snapshot]
  })),

  setStreams: (items) => set({ streams: items }),
  setStreamsDrill: (items) => set({ streamsDrill: items }),
  clearStreamsDrill: () => set({ streamsDrill: [] }),

  setPipelineLatency: (ms) => set({ pipelineLatencyMs: (typeof ms === 'number' && Number.isFinite(ms)) ? ms : null }),
  setPipelineLatencyName: (name) => set({ pipelineLatencyName: name }),
  setNowTs: (ts) => set({ nowTs: ts }),

  setDataDebouncing: (on) => set({ dataDebouncing: on }),
  setStreamsLoading: (on) => set({ streamsLoading: on }),
  setAnalysisLoading: (on) => set({ analysisLoading: on }),
  setLastDataError: (msg) => set({ lastDataError: msg }),
  markStreamsUpdated: (ts) => set({ lastStreamsUpdateTs: ts ?? Date.now() }),
  setSearchResult: (r) => set({ searchResult: r }),
  setWorldFlowResult: (r) => set({ worldFlowResult: r }),
  setSearchResultDrill: (r) => set({ searchResultDrill: r }),

  addIncident: (inc) => set((s) => ({
    incidents: [inc, ...s.incidents.filter((i) => i.id !== inc.id)]
  })),

  setIncidents: (incidents) => set({ incidents }),

  setDimensions: (dims) => {
    return set(() => ({ dimensions: dims } as Partial<AppStore>))
  },

  addComparisonStream: (sid) => set((s) => {
    const cur = s.comparison.streams
    const n = Object.keys(cur).length
    if (cur[sid] || n >= s.comparison.maxItems) return { comparison: s.comparison }
    return {
      comparison: {
        ...s.comparison,
        streams: { ...cur, [sid]: true },
        visible: { ...s.comparison.visible, [sid]: true },
      }
    }
  }),
  removeComparisonStream: (sid) => set((s) => {
    const cur = { ...s.comparison.streams }
    const vis = { ...s.comparison.visible }
    delete cur[sid]
    delete vis[sid]
    return { comparison: { ...s.comparison, streams: cur, visible: vis } }
  }),
  toggleComparisonVisible: (sid) => set((s) => {
    const vis = { ...s.comparison.visible }
    vis[sid] = !vis[sid]
    return { comparison: { ...s.comparison, visible: vis } }
  }),
  clearComparison: () => set((s) => ({ comparison: { ...s.comparison, streams: {}, visible: {} } })),
}))

// ─── Stable selector hooks ────────────────────────────────────────────────────
// These use useShallow so that array identity is stable when contents are equal,
// preventing the infinite re-render loop caused by new array refs on every call.

/** Table / hex source: API slice nodes for the active dimension (or drill stream list). */
function searchNodesForView(s: AppStore): SearchSliceNode[] {
  const drill =
    s.view === 'hex' && s.dimension === 'stream' && s.drillNode
  if (drill) return s.searchResultDrill?.nodes ?? []
  const nodes = s.searchResult?.nodes ?? []

  if (s.view === 'deep') {
    const latest = new Map<string, SearchSliceNode>()
    for (const n of nodes) {
      const key = sliceRowKey(n)
      const existing = latest.get(key)
      if (!existing || (n.windowEndMs ?? 0) > (existing.windowEndMs ?? 0)) {
        latest.set(key, n)
      }
    }
    return [...latest.values()]
  }

  return nodes
}

function computeFiltered(s: AppStore): SearchSliceNode[] {
  return searchNodesForView(s)
}

function computeHex(s: AppStore): SearchSliceNode[] {
  return searchNodesForView(s)
}

// Re-export as hooks — components call useFilteredStreams() not useStore(selector)
export function useFilteredStreams() {
  return useStore(useShallow(computeFiltered))
}

export function useHexStreams() {
  return useStore(useShallow(computeHex))
}

// Keep these as plain selector fns for backwards compat with MetricRow
export const selectFilteredStreams = computeFiltered
export const selectHexStreams = computeHex

export function selectStreamIdsForAnalysis(state: AppStore): string[] {
  const nodes = searchNodesForView(state)
  const ids = nodes
    .filter((n): n is StreamSliceNode => n.kind === 'stream')
    .map((n) => n.streamId || n.value)
    .filter(Boolean)
  return Array.from(new Set(ids)).sort()
}

// Hook variant: returns a reference-stable array via shallow equality so
// subscribers (e.g. DeepDiveView) don't re-render in an infinite loop because
// `selectStreamIdsForAnalysis` builds a fresh array on every call.
export function useStreamIdsForAnalysis() {
  return useStore(useShallow(selectStreamIdsForAnalysis))
}
